import { usePageTitle } from '../hooks/usePageTitle';

export function AccessibilityPage() {
  usePageTitle('Accessibility — Hawkes Bridge');

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-bold text-ink mb-6">
        Accessibility
      </h1>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink mb-3">
          Our commitment
        </h2>
        <p className="text-ink">
          Hawkes Bridge is committed to making this site accessible to people with
          a wide range of abilities. We aim to meet the Web Content Accessibility
          Guidelines (WCAG) 2.1 Level AA across the public, learner-facing parts
          of the site.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink mb-3">
          Conformance status
        </h2>
        <p className="text-ink">
          This site is <strong>partially conformant</strong> with WCAG 2.1 AA.
          Most learner-facing pages meet the success criteria; some game-play
          surfaces have known limitations described below.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink mb-3">
          Known limitations
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-ink">
          <li>
            The Counting Practice card-play animation is primarily visual and is
            not optimised for screen readers. Users who request reduced motion
            via their operating system will not see the animation auto-play; the
            Play button remains available.
          </li>
          <li>
            Bidding tables and hand displays are primarily visual layouts and
            may not be fully navigable with assistive technology.
          </li>
          <li>
            On the "Decision to make a 3rd level bid" practice page, the inline
            editor controls (the data-category dropdown, status labels, Refresh,
            the level-of-difficulty buttons, and the Previous button) are shown
            only to administrators and to editors an administrator has granted
            the "3rd level bid editing" feature. This
            authoring surface is intended for desktop and tablet use and is not
            held to the phone-layout or WCAG criteria that govern the public,
            learner-facing page. Public learners never see these controls; the
            learner-facing page itself remains in full accessibility scope.
          </li>
          <li>
            On Counting Practice, Problems, and the module practice questions
            page, a "rotate to portrait" overlay is shown on small phones in
            landscape orientation (height ≤ 550 px) because the dense game-play
            layout cannot operate in cramped landscape. The page content is
            still rendered underneath the overlay.
          </li>
          <li>
            <strong>Text size on the page awaiting replacement.</strong> We
            hold every page to a minimum rendered text size of 12 CSS pixels
            and avoid all-capitals labels on phones, because neither is
            comfortable for the many members of our audience reading at arm's
            length. One older page does not yet meet that floor — the Howard
            Hands page — where some labels render as small as 8.8 CSS pixels.
            That page is being replaced rather than re-tuned, and its
            replacement meets the floor. You can enlarge any page with your
            browser's zoom in the meantime.
          </li>
          <li>
            On the topic pages, each practice module lists Study, Practice and
            Master. Only <strong>Practice</strong> is built; Study and Master
            are placeholders for features still in development. They are
            rendered as plain text rather than buttons, so they are skipped by
            keyboard navigation and are not announced as controls — they do
            nothing when clicked, by design. The topic's reading material is
            reached from the "Study …" link on the topic's own title line.
          </li>
          <li>
            Inventory topics still in development are marked "COMING SOON" and
            do not open. They are inactive by design and, under the WCAG
            exemption for inactive controls, are not held to the contrast
            minimum.
          </li>
          <li>
            On Counting Practice, the trick-navigation buttons (previous/next
            trick, previous/next card, play/stop) are tappable over at least
            24 × 24 CSS pixels, which meets the WCAG 2.2 AA minimum, but they
            do not meet the WCAG 2.5.5 (AAA) 44 × 44 recommendation — the
            dense game-play layout has to fit a phone screen in portrait
            without scrolling. The one exception is the four arrow buttons on
            the shortest phones (screens about 700 CSS pixels tall or less,
            such as a 360 × 640 handset), where the whole layout is already at
            its smallest and the arrows are about 15 × 15; there they fall
            below the 24 × 24 minimum. Users who find these controls difficult
            to tap may zoom in, use a stylus, or switch to a larger phone,
            tablet or desktop, where they meet the minimum. We will raise the
            arrows on the shortest phones too once our minimum supported
            screen height rises, and this exception may then be removed.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink mb-3">
          Compatibility
        </h2>
        <p className="text-ink mb-2">
          This site is tested against the latest two major versions of:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-ink">
          <li>Google Chrome — desktop and mobile</li>
          <li>Mozilla Firefox — desktop and mobile</li>
          <li>Apple Safari — macOS and iOS</li>
          <li>Microsoft Edge — desktop</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink mb-3">Feedback</h2>
        <p className="text-ink">
          If you encounter an accessibility barrier on this site or need an
          accommodation, please email us at{' '}
          <a
            href="mailto:support@writersmuse.com"
            className="text-primary-700 underline hover:text-primary-900"
          >
            support@writersmuse.com
          </a>
          . We aim to respond within five working days.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink mb-3">
          Last reviewed
        </h2>
        <p className="text-ink">
          <time dateTime="2026-09-04">2026-09-04</time>
        </p>
      </section>
    </div>
  );
}
